
import './synchro-modal.scss'

import { tap } from 'rxjs/operators'
import { DateTime } from 'luxon'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { faCogs, faBoxOpen, faCheck, faCloudDownloadAlt, faCloudUploadAlt, faEdit, faExclamationTriangle, faInfoCircle, faPlusCircle, faSlash, faSyncAlt, faTabletAlt, faTimes, faToggleOff, faToggleOn, faWifi } from '@fortawesome/free-solid-svg-icons'

import { DataSynchronisationStatus } from '@skinsoft/types'
import { SynchroDownHandler, SynchroUpHandler } from "@skinsoft/classes-interfaces-definition";
import { formatDateFromNow, formatDuration } from '@skinsoft/utils'
import { DataSynchronizerStats, OfflineDataFetcher, OfflineDataUpdateCacher } from '@skinsoft/offline'
import { 
    AuditCampaignDataStore, 
    ItemDataStore, 
    RecordDataStore,
} from '@skinsoft/entity'
import { ImageTransferHelper } from '@skinsoft/media-transfer-helper'
import { 
    dataSynchroEventBus$,
    AliasSyncDownHandler,
    AuditCampaignSyncDownHandler, 
    AuditRecordSyncDownHandler, 
    AuthoritySyncDownHandler, 
    ConditionReportSyncDownHandler, 
    EntityFieldSyncDownHandler, 
    LocationSyncDownHandler, 
    ThesaurusSyncDownHandler, 
    ThesaurusTermSyncDownHandler, 
    WemiSynchroDownHandler,
} from '@skinsoft/synchro-down-handlers'
import {
    ConditionReportSyncUpHandler,
} from '@skinsoft/synchro-up-handlers'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus'
import { OnlineStatusIndicator } from '../online-status-indicator'
import { SynchroView } from '../synchro-view'

interface SynchDownHandlerWrapper {
    title: string,
    synchDownHandler: SynchroDownHandler,
}

interface SynchUpHandlerWrapper {
    title: string,
    synchUpHandler: SynchroUpHandler,
}

interface SynchroUpHandlerStats {
    total: number
    nbCreations: number
    nbUpdates: number
}

declare const config

declare const SYNCH_UP_HANDLERS: { title: string, synchUpHandler: string }[]

declare const SYNCH_DOWN_HANDLERS: {title: string, synchDownHandler: string}[]

const CLEAN_GOHST_IMAGE_BEFORE_SYNC_DOWN: boolean = config.gohstImageCleaningSettings.beforeSyncDown
const CLEAN_GOHST_IMAGE_AFTER_SYNC_UP: boolean = config.gohstImageCleaningSettings.afterSyncUp
const RUN_SYNC_DOWN_PARALLEL: boolean = config.syncDownParallel

@Component({
    components: {
        sOnlineStatusIndicator: OnlineStatusIndicator,
        sSynchroView: SynchroView
    },
    props: [''],
    template: require('./synchro-modal.html')
})
export class SynchroModal extends ReactiveVue {

    public $refs: {
        modal// Modal bootstrap (for some reason we cannot import type, maybe missing @types for bootstrap in package.json)
    }

    // Synchro 
    private synchDownHandlerWrappers: SynchDownHandlerWrapper[] = []
    private synchUpHandlersWrappers: SynchUpHandlerWrapper[] = []
    private nbSynchroCompleted: number = 0;
    private errorInGlobalSynchro: boolean = false;
    private progressBarVariant: string = "success";
    private globalSyncing: boolean = false;
    private executionStartTime: DateTime | null = null;
    private lastSynchroStats: DataSynchronizerStats = null as any;
    private showDownloadDetails: boolean = false;

    // Push
    private pushResults: any[] = [];
    private nbEntitiesInUpdateCache: number = 0;
    private pushAllStatus: DataSynchronisationStatus = DataSynchronisationStatus.SUCCESS;
    private nbPushesCompleted: number = 0;
    private nbSuccesfullPushes: number = 0;
    private lastPushStats: any = { date: 0, status: DataSynchronisationStatus.SUCCESS };
    private globalPushing: boolean = false;
    private showUploadDetails: boolean = false;
    private pushProgressBarVariant = "success";
    private synchroUpHandlersStats: SynchroUpHandlerStats[] = []

    showClearCacheDownDialog = false
    showClearCacheUpDialog = false

    // Icons
    private syncIcon = faSyncAlt;
    private crossIcon = faTimes;
    private wifiIcon = faWifi;
    private slashIcon = faSlash;
    private downloadIcon = faCloudDownloadAlt;
    private uploadIcon = faCloudUploadAlt;
    private infoIcon = faInfoCircle;
    private warningIcon = faExclamationTriangle;
    private localIcon = faTabletAlt;
    private switchOnIcon = faToggleOn;
    private switchOffIcon = faToggleOff;
    private checkIcon = faCheck;
    private statusErrorIcon = faTimes;
    private emptyIcon = faBoxOpen;
    private editIcon = faEdit;
    private plusIcon = faPlusCircle;
    private menuIcon = faCogs

    private $imageTransferHelper: ImageTransferHelper

    private setupSynchroDownHandlers() {
        if(SYNCH_DOWN_HANDLERS) {
            let synchroHandlersWrappers: (SynchDownHandlerWrapper | null)[] = []

            synchroHandlersWrappers = SYNCH_DOWN_HANDLERS.map(config => {
                let synchroDownHandler: SynchroDownHandler

                switch(config.synchDownHandler) {
                    case "WemiSynchroDownHandler":
                        synchroDownHandler = new WemiSynchroDownHandler()
                        break;
                    case "ConditionReportSyncDownHandler":
                        synchroDownHandler = ConditionReportSyncDownHandler.getInstance();
                        break;
                    case "AuditRecordSyncDownHandler":
                        synchroDownHandler = AuditRecordSyncDownHandler.getInstance();
                        break;
                    case "AuditCampaignSyncDownHandler":
                        synchroDownHandler = AuditCampaignSyncDownHandler.getInstance();
                        break;
                    case "ThesaurusTermSyncDownHandler":
                        synchroDownHandler = ThesaurusTermSyncDownHandler.getInstance();
                        break;
                    case "ThesaurusSyncDownHandler":
                        synchroDownHandler = ThesaurusSyncDownHandler.getInstance();
                        break;
                    case "EntityFieldSyncDownHandler":
                        synchroDownHandler = EntityFieldSyncDownHandler.getInstance();
                        break;
                    case "AliasSyncDownHandler":
                        synchroDownHandler = AliasSyncDownHandler.getInstance();
                        break;
                    case "AuthoritySyncDownHandler":
                        synchroDownHandler = AuthoritySyncDownHandler.getInstance();
                        break;
                    case "LocationSyncDownHandler":
                        synchroDownHandler = LocationSyncDownHandler.getInstance();
                        break;
                    default: 
                        return null;
                }

                return {
                    title: config.title,
                    synchDownHandler: synchroDownHandler,
                }
            })

            this.synchDownHandlerWrappers = synchroHandlersWrappers.filter(wrapper => {
                return wrapper != null
            }) as SynchDownHandlerWrapper[]
        }
    }

    /*
     * sets this.dataStores with the configuration defined in vue.config.js
    */
    private setupSynchroUpHandlers() {
        if(SYNCH_UP_HANDLERS) {
            let wrappers: Array<SynchUpHandlerWrapper | null>;
            wrappers = SYNCH_UP_HANDLERS.map(config => {
                // @ts-ignore
                let synchUpHandler: SynchroUpHandler

                switch(config.synchUpHandler) {
                    case "ConditionReportSyncUpHandler":
                        synchUpHandler = ConditionReportSyncUpHandler.getInstance();
                        break;
                    case "ItemDataStore":
                        synchUpHandler = ItemDataStore.getInstance();
                        break;
                    case "RecordDataStore":
                        synchUpHandler = RecordDataStore.getInstance();
                        break;
                    case "AuditCampaignDataStore":
                        synchUpHandler = AuditCampaignDataStore.getInstance();
                        break;
                    default: 
                        return null;
                }

                return { title: config.title, synchUpHandler: synchUpHandler }
            })

            this.synchUpHandlersWrappers = wrappers.filter(wrapper => {
                return wrapper != null
            }) as SynchUpHandlerWrapper[]
        }
    }

    public mounted() {
        this.setupSynchroDownHandlers()
        this.setupSynchroUpHandlers()
        this.fetchLastSynchroStats()
        this.getNbEntitiesInUpdateCache()
        this.initSyncUpHandlerStats()
        dataSynchroEventBus$.subscribe(
            async event => {
                if (event.status === DataSynchronisationStatus.SUCCESS) {
                    this.nbSynchroCompleted++;
                    // All sychros are completed
                    if (this.nbSynchroCompleted === this.nbSubSynchros) {
                        await this.saveGlobalStats(DataSynchronisationStatus.SUCCESS)
                        this.globalSyncing = false;
                    }
                } else if (event.status === DataSynchronisationStatus.ERROR) {
                    this.errorInGlobalSynchro = true;
                    this.progressBarVariant = "danger";
                    await this.saveGlobalStats(event.status)
                    this.globalSyncing = false;
                }
            }
        )
    }

    public open() {
        this.$refs.modal.show()
        this.getNbEntitiesInUpdateCache()
        // Need to refresh time-based properties
        this.$forceUpdate();
    }

    // Fetching data
    public async syncAll() {
        if(CLEAN_GOHST_IMAGE_BEFORE_SYNC_DOWN) {
            await this.$imageTransferHelper.cleanCachedImages()
        }

        this.executionStartTime = DateTime.now();
        this.globalSyncing = true;
        this.nbSynchroCompleted = 0;
        this.progressBarVariant = "success";
        this.errorInGlobalSynchro = false;
        for (const synchDownHandlerWrapper of this.synchDownHandlerWrappers) {
            if(RUN_SYNC_DOWN_PARALLEL) {
                synchDownHandlerWrapper.synchDownHandler.synchronize(0, true, true)
            } else {
                await synchDownHandlerWrapper.synchDownHandler.synchronize(0, true, true)
            }
        }
    }

    public async abortAll() {
        await this.saveGlobalStats(DataSynchronisationStatus.ABORTED)
        this.progressBarVariant = "danger";
        for (const synchDownWrapper of this.synchDownHandlerWrappers) {
            synchDownWrapper.synchDownHandler.abortSynchronisation()
        }
        this.globalSyncing = false;
    }

    private async saveGlobalStats(status: DataSynchronisationStatus) {
        // Saving global sync stats to cache
        await OfflineDataFetcher.saveGlobalSyncStats({
            currentNbSychronized: 0,
            lastSynchroDate: DateTime.now().toMillis(),
            lastSynchroDuration: this.executionStartTime ? DateTime.now().diff(this.executionStartTime).toMillis() : 0,
            lastSynchroStatus: status,
            maxNbToSynchronize: 0,
        })
        this.fetchLastSynchroStats()
    }

    /**
     * Retrieve last synchro and push stats from cache
     */
    private async fetchLastSynchroStats() {
        this.lastSynchroStats = await OfflineDataFetcher.getLastGlobalSyncStats()
        this.lastPushStats = await OfflineDataUpdateCacher.getLastPushStats();
    }

    private get lastSynchroDuration() {
        return formatDuration(
            this.lastSynchroStats?.lastSynchroDuration,
            this.$t("time.minutes"),
            this.$t("time.seconds")
        ) || "..."
    }

    private get lastSynchroDate() {
        const formattedDate = formatDateFromNow(this.lastSynchroStats?.lastSynchroDate, this.getAppLocale())
        return formattedDate !== "" ? formattedDate || "..." : "...";
    }

    private get lastSynchroStatus() {
        return this.lastSynchroStats?.lastSynchroStatus ?
            this.$t(`state.${this.lastSynchroStats.lastSynchroStatus}`) :
            "...";
    }

    private get lastPushStatus() {
        return this.lastPushStats?.status ?
            this.$t(`state.${this.lastPushStats.status}`) :
            "...";
    }


    private get formattedLastPushDate() {
        const formattedDate = formatDateFromNow(this.lastPushStats?.date, this.getAppLocale())
        return formattedDate !== "" ? formattedDate : "...";
    }

    private statusIcon(status: DataSynchronisationStatus) {
        if (status) {
            if (status === DataSynchronisationStatus.SUCCESS) {
                return faCheck;
            } else {
                return faTimes;
            }
        }
        return null;
    }

    private statusIconDataCy(status: DataSynchronisationStatus) {
        if (status) {
            if (status === DataSynchronisationStatus.SUCCESS) {
                return "synchro-modal--sync-up--sucess-icon"
            } else {
                return "synchro-modal--sync-up--failure-icon"
            }
        }
        return ""
    }

    private statusIndicatorStyle(status: DataSynchronisationStatus) {
        if (status) {
            if (status === DataSynchronisationStatus.SUCCESS) {
                return "status-success";
            } else {
                return "status-error";
            }
        }
        return "";
    }

    private get nbSubSynchros() {
        return this.synchDownHandlerWrappers.length
    }


    private async getNbEntitiesInUpdateCache() {
        this.nbEntitiesInUpdateCache = 0;
        for (const [index, syncUpHandlerWrapper] of this.synchUpHandlersWrappers.entries()) {
            const nbCachedEntities = await syncUpHandlerWrapper.synchUpHandler.getNbCachedObjects();
            this.nbEntitiesInUpdateCache += nbCachedEntities

            const syncUpHandlerStats = await this.getSynchroUpHandlerStats(syncUpHandlerWrapper.synchUpHandler)

            this.synchroUpHandlersStats.splice(
                index, 
                1,
                syncUpHandlerStats
            )
        }
    }

    private async initSyncUpHandlerStats() {
        for(const wrapper of this.synchUpHandlersWrappers) {
            const stats = await this.getSynchroUpHandlerStats(wrapper.synchUpHandler)

            this.synchroUpHandlersStats.push(stats)
        }
    }

    private async getSynchroUpHandlerStats(synchroUpHandler: SynchroUpHandler): Promise<SynchroUpHandlerStats> {
        const totalEntity = await synchroUpHandler.getNbCachedObjects()
        const nbCreations = await synchroUpHandler.getNbLocallyCreatedEntities() 

        return {
            total: totalEntity,
            nbCreations: nbCreations,
            nbUpdates: totalEntity - nbCreations,
        }
    }

    private get nbDataPushers() {
        return this.synchUpHandlersWrappers.length;
    }

    // Uploading data
    private async pushAll() {
        this.nbSuccesfullPushes = 0;
        this.nbPushesCompleted = 0;
        let totalNbInCache = 0;

        this.globalPushing = true;
        this.pushResults = [];


        for (const syncUpHandlerWrapper of this.synchUpHandlersWrappers) {
            totalNbInCache += await syncUpHandlerWrapper.synchUpHandler.getNbCachedObjects()
        }

        for(const syncUpHandlerWrapper of this.synchUpHandlersWrappers) {
            const synchroUpRes = await (await syncUpHandlerWrapper.synchUpHandler.handleSynchroUpdateFromCache(undefined, this.getAppLocale())).pipe(
                tap(success => {
                    this.onDataStorePushCompleted(success, totalNbInCache)

                }),
            ).toPromise()
        }
    }

    private async onDataStorePushCompleted(updateSuccess: boolean, totalNbInCache: number) {
        this.nbPushesCompleted++;
        if (updateSuccess) {
            this.nbSuccesfullPushes++;
        }

        // All updates have been performed (with success or not)
        if (this.nbPushesCompleted === totalNbInCache) {
            if (this.nbPushesCompleted !== this.nbSuccesfullPushes) {
                this.pushAllStatus = DataSynchronisationStatus.ERROR;
                this.pushProgressBarVariant = "danger";
            } else {
                this.pushAllStatus = DataSynchronisationStatus.SUCCESS;
                this.pushProgressBarVariant = "sucess";
            }
            this.getNbEntitiesInUpdateCache()
            // Saving push stats
            const now = DateTime.now().toMillis()
            await OfflineDataUpdateCacher.setLastPushStats(now, this.pushAllStatus);
            this.fetchLastSynchroStats()
            this.globalPushing = false;
        }
        
        if(CLEAN_GOHST_IMAGE_AFTER_SYNC_UP) {
            await this.$imageTransferHelper.cleanCachedImages()
        }
    }

    private abortPushAll() {
        this.globalPushing = false;
        for (const wrapper of this.synchUpHandlersWrappers) {
            wrapper.synchUpHandler.abortPush();
        }
    }

    private async onClearImageCacheClicked() {
        await this.$imageTransferHelper.cleanCachedImages()
        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
            message: this.$t('synchroModal.cachedImagedCleaned'),
            variant: "info"
        });
    }

    private toggleClearCacheDownDialog() {
        this.showClearCacheDownDialog = !this.showClearCacheDownDialog
    }

    private toggleClearCacheUpDialog() {
        this.showClearCacheUpDialog = !this.showClearCacheUpDialog
    }

    private async clearCacheDown() {
        for (const synchDownWrapper of this.synchDownHandlerWrappers) {
            await synchDownWrapper.synchDownHandler.clearCacheDown()
        }
        //await InstitutionDataStore.getInstance().clearCacheDown()
        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
            message: this.$t('synchroModal.cacheDownCleaned'),
            variant: 'warning',
        })
    }

    private async clearCacheUp() {
        await OfflineDataUpdateCacher.clear()
        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
            message: this.$t('synchroModal.cacheUpCleaned'),
            variant: 'warning',
        })
    }

    @Watch("globalSyncing")
    private onGlobalSyncingChange() {
        if (!this.globalSyncing) {
            this.$emit("sync-finished");
        }
    }

    @Watch("globalPushing")
    private onGlobalPushingChange() {
        if (!this.globalPushing) {
            this.$emit("sync-finished");
            this.emitOnBus('push-synchro-performed')
        }
    }
}